import { motion } from "framer-motion";
import { Youtube, Play, Bell } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import { trackClick } from "@/lib/api";

const EASE = [0.16, 1, 0.3, 1];
const CHANNEL = "https://www.youtube.com/channel/UCIOdYZDaejW4qKL9eljLfmA";
const FEATURED = "Lidv9mZAdH4";

export default function WatchSection() {
  return (
    <section
      id="watch"
      data-testid="watch-section"
      className="grain relative overflow-hidden bg-emerald-ink py-24 lg:py-32"
    >
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(1000px 560px at 50% -10%, rgba(19,78,61,0.9), transparent 65%), radial-gradient(700px 400px at 92% 90%, rgba(212,175,55,0.1), transparent 60%)",
        }}
        aria-hidden="true"
      />
      <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <div className="text-center [&>div]:mx-auto">
          <SectionHeading
            dark
            testId="watch-heading"
            eyebrow="Now Showing"
            title={
              <>
                Watch the Fables <em className="text-gold">Come Alive.</em>
              </>
            }
            sub="Narrated fable films from the Panchatantra Tales, animated in the same cinematic style as the books — free on our YouTube channel, with more languages on the way."
          />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="relative mt-14"
        >
          <div className="absolute -inset-3 border border-gold/25" aria-hidden="true" />
          <div
            className="pointer-events-none absolute -inset-10 rounded-full opacity-60"
            style={{
              background: "radial-gradient(closest-side, rgba(212,175,55,0.16), transparent 75%)",
            }}
            aria-hidden="true"
          />
          <div className="relative aspect-video w-full overflow-hidden rounded-sm border border-gold/40 bg-emerald-deep shadow-lift">
            <iframe
              src={`https://www.youtube-nocookie.com/embed/${FEATURED}?rel=0`}
              title="El Mono y el Cocodrilo — Panchatantra fable narrated in Spanish"
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="absolute inset-0 h-full w-full"
              data-testid="watch-player"
            />
          </div>
          <div className="mt-6 flex flex-col items-center gap-2 text-center">
            <p className="flex items-center gap-2 font-serif text-xl italic text-cream" data-testid="watch-featured-title">
              <Play className="h-4 w-4 fill-gold text-gold" aria-hidden="true" />
              El Mono y el Cocodrilo — narrated in Spanish
            </p>
            <p className="text-sm text-cream/55">
              From Vol. V · The Monkey and the Crocodile · Panchatantra Tales
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.15, ease: EASE }}
          className="mt-10 flex flex-col items-center gap-4"
        >
          <a
            href={`${CHANNEL}?sub_confirmation=1`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="watch-subscribe-btn"
            onClick={() => trackClick("watch-subscribe", CHANNEL, "watch")}
            className="flex items-center gap-2.5 rounded-full bg-gold px-8 py-4 font-mono text-xs font-semibold uppercase tracking-[0.15em] text-emerald-ink transition-transform duration-300 hover:-translate-y-0.5 hover:shadow-glow"
          >
            <Bell className="h-4 w-4" aria-hidden="true" />
            Subscribe on YouTube
          </a>
          <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-cream/45">
            <Youtube className="h-3.5 w-3.5 text-gold/70" aria-hidden="true" />
            New fable films in every language — coming soon
          </p>
        </motion.div>
      </div>
    </section>
  );
}
